import React, { useState } from 'react';
import tw, { styled } from 'twin.macro';

import {
  Flex,
  Panel,
  PageWrapper,
  OneHalfLayout
} from '@sopranodesign/pulse-components';
import {
  useAuth,
  FEATURE_GATES,
  FeatureFilter,
  GlobalRoleFilter,
  GlobalFeatureGateFilter
} from '@sopranodesign/pulse-auth';
import { ConditionalTypeProvider } from '@sopranodesign/pulse-conditionals';
import { BackButton } from '@sopranodesign/pulse-channels';

import { BotSettingsProvider } from '../Providers';
import {
  FontCode,
  SaveSettings,
  WidgetPreview,
  AvatarSettings,
  DeploymentCode,
  DisplaySettings,
  ComposerSettings,
  SettingPageSettings,
  CallToActionSettings,
  ConverseBannerSettings,
  ConverseColourSettings,
  ConverseFooterSettings,
  ConverseGeneralSettings,
  ConverseWidgetSizeSettings
} from '../Components/index';
import { updateConverseLogo, updateConverseSettings } from '../api';
import {
  CreateWidget,
  UpdateWidget,
  DuplicateWidget
} from './Widget/Components/index';
import AllTabs from '../Components/Converse/AllTabs';
import TabAdminDropdown from '../Components/Converse/TabAdminDropdown';
import SettingsTabs from '../Components/Shared/SettingsTabs';

const StyledContainer = styled.div`
    ${tw` flex flex-auto flex-col-reverse xl:flex-row xl:justify-start`}
`;

const StyledPanel = styled(Panel)`
    border-top-left-radius: 0;
`;

const ConverseSettings = () => {
  const [showUpdateWidget, setShowUpdateWidget] = useState(false);
  const [showDuplicateWidget, setShowDuplicateWidget] = useState(false);
  const [showWidgetCreator, setShowWidgetCreator] = useState(false);
  const [settingSection, setSettingSection] = useState('main');

  const { client } = useAuth();

  const source = `${process.env.REACT_APP_DEPLOYMENT_ROOT_URL}?c=${process.env.REACT_APP_INSTALL_NAME}&i=${client.identifier}`;

  const deployment = {
    script: `<script src="${source}" id="chat-widget-1925"></script>`,
    info:
      'Paste this snippet in the <body> of your website.  Please note, this only needs to be done once.  All subsequent changes are saved and deployed automatically.',
    src: source
  };

  return (
    <PageWrapper
      header="Converse widgets"
      prevAction={<BackButton to="/channels" />}
      subheader="Manage the appearance of your Converse widget(s)"
    >
      <BotSettingsProvider
        type="converse"
        updateBotLogo={updateConverseLogo}
        updateBotSettings={updateConverseSettings}
        deploymentDetails={deployment}
      >
        <AllTabs
          showWidgetCreator={showWidgetCreator}
          setShowWidgetCreator={setShowWidgetCreator}
        />
        {showWidgetCreator && (
          <CreateWidget
            handleClose={() => setShowWidgetCreator(!showWidgetCreator)}
          />
        )}
        {showUpdateWidget && (
          <UpdateWidget
            handleClose={() => setShowUpdateWidget(!showUpdateWidget)}
          />
        )}
        {showDuplicateWidget && (
          <DuplicateWidget
            handleClose={() => setShowDuplicateWidget(!showDuplicateWidget)}
          />
        )}
        <OneHalfLayout>
          <StyledPanel>
            <SettingsTabs
              section={settingSection}
              onChange={setSettingSection}
              sections={[
                { key: 'main', label: 'Main' },
                { key: 'engagement', label: 'Engagement' },
                { key: 'design', label: 'Design' },
                { key: 'advanced', label: 'Advanced' }
              ]}
              adminSelect={
                <TabAdminDropdown
                  right
                  setShowUpdateWidget={setShowUpdateWidget}
                  showUpdateWidget={showUpdateWidget}
                  setShowDuplicateWidget={setShowDuplicateWidget}
                  showDuplicateWidget={showDuplicateWidget}
                  data-testid="tab-admin-dropdown"
                />}
            />
            <StyledContainer>
              <Flex col gap growShrink>
                {settingSection === 'main' && (
                  <>
                    <DeploymentCode hideTitle />
                    <ConverseGeneralSettings />
                  </>
                )}

                {settingSection === 'engagement' && (
                  <>
                    <ConverseBannerSettings />
                    <ConditionalTypeProvider>
                      <CallToActionSettings />
                    </ConditionalTypeProvider>
                    <ComposerSettings />
                    <ConditionalTypeProvider>
                      <DisplaySettings />
                    </ConditionalTypeProvider>
                  </>
                )}

                {settingSection === 'design' && (
                  <>
                    <ConverseColourSettings />
                    <ConverseWidgetSizeSettings />
                    <GlobalFeatureGateFilter for={FEATURE_GATES.WHITELABEL_FOOTER}>
                      <FeatureFilter feature="whitelabel footer">
                        <GlobalRoleFilter for="owner">
                          <ConverseFooterSettings data-testid="converse footer settings" />
                        </GlobalRoleFilter>
                      </FeatureFilter>
                    </GlobalFeatureGateFilter>
                    <FontCode />
                    <AvatarSettings />
                  </>
                )}

                {settingSection === 'advanced' && (
                  <>
                    <SettingPageSettings />
                  </>
                )}

                <SaveSettings />
              </Flex>
            </StyledContainer>
          </StyledPanel>
        </OneHalfLayout>
        <OneHalfLayout>
          <WidgetPreview />
        </OneHalfLayout>
      </BotSettingsProvider>
    </PageWrapper>
  );
};

export default ConverseSettings;
